export interface AppErrorCaptureContext {
  routeKind: 'org_thread' | 'other';
  orgThreadRef?: string;
}

export interface AppErrorCaptureProperties extends AppErrorCaptureContext {
  error_operation: string;
  error_boundary: 'next_route_error_boundary' | 'next_global_error_boundary';
  error_code: 'CONVEX_CLIENT_ERROR' | 'NEXTJS_ROUTE_ERROR';
  convex_function?: string;
  convex_function_type?: 'action' | 'mutation' | 'query' | 'unknown';
  request_id?: string;
}

const ORG_THREAD_ROUTE_PATTERN =
  /^\/[^/]+\/threads\/(ot_[A-Za-z0-9_-]+)(?:\/|$)/;
const CONVEX_FAILURE_PATTERN =
  /\[CONVEX ([A-Z]+)\(([^)]+)\)\](?:\s+\[Request ID:\s*([A-Za-z0-9_-]+)\])?/;
const SAFE_CONVEX_FUNCTION_PATTERN = /^[A-Za-z0-9_./:-]{1,180}$/;

type ConvexFunctionType = NonNullable<
  AppErrorCaptureProperties['convex_function_type']
>;

const convexFunctionType = (functionType: string): ConvexFunctionType => {
  if (functionType === 'Q') return 'query';
  if (functionType === 'M') return 'mutation';
  if (functionType === 'A') return 'action';
  return 'unknown';
};

export interface ConvexClientFailure {
  convexFunction: string;
  functionType: ConvexFunctionType;
  requestId?: string;
}

/** The function a Convex client error names (`[CONVEX M(path)]`), if safe. */
export const parseConvexClientFailure = (
  message: string
): ConvexClientFailure | null => {
  const convexFailure = message.match(CONVEX_FAILURE_PATTERN);
  const functionType = convexFailure?.[1];
  const convexFunction = convexFailure?.[2];
  if (
    !functionType ||
    !convexFunction ||
    !SAFE_CONVEX_FUNCTION_PATTERN.test(convexFunction)
  )
    return null;
  const requestId = convexFailure?.[3];
  return {
    convexFunction,
    functionType: convexFunctionType(functionType),
    ...(requestId ? { requestId } : {}),
  };
};

export const buildAppErrorCaptureContext = (
  pathname: string | null
): AppErrorCaptureContext => {
  const threadRoute = pathname?.match(ORG_THREAD_ROUTE_PATTERN);
  const orgThreadRef = threadRoute?.[1];

  return orgThreadRef
    ? { routeKind: 'org_thread', orgThreadRef }
    : { routeKind: 'other' };
};

export const buildAppErrorCaptureProperties = (
  error: Error,
  pathname: string | null,
  boundary: AppErrorCaptureProperties['error_boundary'] = 'next_route_error_boundary'
): AppErrorCaptureProperties => {
  const routeContext = buildAppErrorCaptureContext(pathname);
  const failure = parseConvexClientFailure(error.message);

  if (!failure) {
    return {
      ...routeContext,
      error_operation: boundary,
      error_boundary: boundary,
      error_code: 'NEXTJS_ROUTE_ERROR',
    };
  }
  const { convexFunction, functionType, requestId } = failure;

  return {
    ...routeContext,
    error_operation: convexFunction,
    error_boundary: boundary,
    error_code: 'CONVEX_CLIENT_ERROR',
    convex_function: convexFunction,
    convex_function_type: functionType,
    ...(requestId ? { request_id: requestId } : {}),
  };
};
